import { TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { style } from 'typestyle';
import { Messages } from '~src/codegen/Localize';
import { FlowScheduleSelector } from '~src/flow/components/FlowScheduleSelector';
import { FlowBaseInfo } from '~src/ts/models/FlowInfo';
import { isLinkedTaskFlowState, LinkedTaskFlowInitialState } from '../utils/LinkedTaskHelpers';
import { FlowLinkedTaskOptions, FlowLinkedTaskOptionsProps } from './FlowLinkedTaskOptions';
import { LinkedTaskDialogStateContext } from './LinkedTaskDialogStateContextProvider';
import { LinkedTaskItem, LinkedTaskItemProps } from './LinkedTaskItem';
import { LinkedTaskItemSelector, LinkedTaskItemSelectorProps } from './LinkedTaskItemSelector';
import { LinkedTasksDialogFooter, LinkedTasksDialogFooterProps } from './LinkedTasksDialogFooter';

const wrapperStyle = style({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const scrollContainer = style({
  overflow: 'auto',
  height: '100%',
});

const taskListStyle = style({
  marginTop: `${TabStyles.Spacing.MediumSpace}px`,
  flex: '1 1 auto',
  $nest: {
    '> div': {
      marginBottom: `${TabStyles.Spacing.MediumSpace}px`,
    }
  },
});

export type LinkedTasksDialogContentProps = {
  flowInfo: FlowBaseInfo;
  dismiss: () => void;
}

export function LinkedTasksDialogContent(props: LinkedTasksDialogContentProps): JSX.Element {
  const [state, dispatch] = React.useContext(LinkedTaskDialogStateContext);

  React.useEffect(() => {
    // Set up state
    dispatch({ type: 'addFlow', payload: { value: LinkedTaskFlowInitialState(props.flowInfo.id, props.flowInfo.name) }});
    dispatch({ type: 'addNextTask' });
  }, [props.flowInfo]);

  function renderItems(): JSX.Element[] {
    const items: JSX.Element[] = [];
    state.items.map((item, index) => {
      let expansionContent: JSX.Element;
      let flowId: string;
      let flowName: string;
      if (isLinkedTaskFlowState(item)) {
        const taskOptionsProps: FlowLinkedTaskOptionsProps = {
          index: index,
          dispatch: dispatch,
          flowLinkedTaskState: item,
        };
        expansionContent = <FlowLinkedTaskOptions {...taskOptionsProps}/>;
        flowName = item.flowName;
        flowId = item.flowId;
      }
      const selectorProps: LinkedTaskItemSelectorProps = {
        index: index,
        id: props.flowInfo.id, // todo should this change?
        selectedItemId: flowId,
        selectedItemName: flowName,
        contentType: 'FLOW',
        lineageRelationshipType: 'DOWNSTREAM'
      };
      const itemProps: LinkedTaskItemProps = {
        title: Messages.linkedTasksRunFlow(),
        content: <LinkedTaskItemSelector {...selectorProps} />,
        number: index,
        buttonType: flowName ? 'edit' : 'delete',
        expansionContent: expansionContent,
        onDelete: () => dispatch({type: 'deleteTaskEntry', value: index})
      };
      items.push(<LinkedTaskItem {...itemProps} key={index}/>);
    });
    return items;
  }

  function onAddNextTask() {
    dispatch({type: 'addNextTask'});
  }

  const footerProps: LinkedTasksDialogFooterProps = {
    addNextTask: onAddNextTask,
    cancel: props.dismiss,
    createTask: () => null,
  };

  return (
    <div className={wrapperStyle}>
      <div className={scrollContainer}>
        <FlowScheduleSelector scheduleId={state.selectedSchedule} dispatch={dispatch} label={Messages.linkedTasksSchedule()} forLinkedTasks={true}/>
        <div className={taskListStyle}>
          {renderItems()}
        </div>
      </div>
      <LinkedTasksDialogFooter {...footerProps}/>
    </div>
  );
}
